import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  memo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CategoryButton,
  SubMenuItem,
  SubMenuPanel,
  SubMenuPanelItem,
  MenuLink,
  ScrollContainer,
  Layout,
  Content,
} from "./MainMenuBar.style";

import { getMenuCategories } from "../../MainMenu.config";
export * from "../../MainMenu.config";

const MENU_SCROLL_POSITION_KEY = "menuBarScrollLeft";

// 子リンクコンポーネント
const CustomMenuLink = ({
  title,
  to,
  icon,
  clickedIcon,
  activePath,
  onClick,
}) => {
  const url = useLocation().pathname;
  const isActive =
    to !== "/" && (activePath === to || url === to || url.startsWith(`${to}/`));
  const onClickMenuItem = (toPage) => () => {
    if (toPage === "/") {
      sessionStorage.clear();
    } else {
      onClick?.();
    }
  };
  return (
    <SubMenuPanelItem $active={isActive}>
      <MenuLink
        to={to}
        onClick={onClickMenuItem(to)}
        aria-current={isActive ? "page" : undefined}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isActive ? icon : clickedIcon}
          <span>{title}</span>
        </span>
      </MenuLink>
    </SubMenuPanelItem>
  );
};

function MenuBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);

  // 一つだけ開くメニューのキーをstateで管理
  const [openKey, setOpenKey] = useState(() => {
    const saved = sessionStorage.getItem("openKey");
    return saved || null;
  });

  // クリック中の子メニューのpathをstateで管理（初期値は現在のURLパス）
  const [activePath, setActivePath] = useState(location.pathname);

  // URL変化があればactivePathも更新
  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  // openKey を sessionStorage に保存
  useEffect(() => {
    if (openKey) {
      sessionStorage.setItem("openKey", openKey);
    } else {
      sessionStorage.removeItem("openKey");
    }
  }, [openKey]);

  // ページ遷移でMenuBarが再生成されても、親メニューのスクロール位置を維持する
  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const savedScrollLeft = Number(
      sessionStorage.getItem(MENU_SCROLL_POSITION_KEY),
    );
    if (Number.isFinite(savedScrollLeft)) {
      scrollContainer.scrollLeft = savedScrollLeft;
    }

    const saveScrollPosition = () => {
      sessionStorage.setItem(
        MENU_SCROLL_POSITION_KEY,
        String(scrollContainer.scrollLeft),
      );
    };

    scrollContainer.addEventListener("scroll", saveScrollPosition, {
      passive: true,
    });

    return () => {
      // ログアウト時はセッションがクリア済みなので位置を保存しない
      if (sessionStorage.getItem("userId")) {
        saveScrollPosition();
      }
      scrollContainer.removeEventListener("scroll", saveScrollPosition);
    };
  }, []);

  const saveCurrentScrollPosition = () => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem(
        MENU_SCROLL_POSITION_KEY,
        String(scrollContainerRef.current.scrollLeft),
      );
    }
  };

  // メニューの開閉切替
  const toggleOpen = (category) => {
    const firstPath = category.items[0]?.path;
    if (openKey === category.key && location.pathname === firstPath) {
      setOpenKey(null);
      sessionStorage.removeItem("openKey");
      return;
    }
    setOpenKey(category.key);
    sessionStorage.setItem("openKey", category.key);
    if (firstPath) navigate(firstPath);
  };

  const userFlg = sessionStorage.getItem("userFlg");
  const menuCategories = getMenuCategories(userFlg);
  const openCategory = menuCategories.find(
    (category) => category.key === openKey,
  );

  return (
    <Layout onPointerDownCapture={saveCurrentScrollPosition}>
      <Content>
        {openCategory && (
          <SubMenuPanel aria-label={`${openCategory.title} サブメニュー`}>
            {openCategory.items.map((item) => (
              <CustomMenuLink
                key={item.title}
                title={item.title}
                to={item.path}
                icon={item.icon}
                clickedIcon={item.clickedIcon}
                activePath={activePath}
                onClick={() => setActivePath(item.path)}
              />
            ))}
          </SubMenuPanel>
        )}

        <ScrollContainer ref={scrollContainerRef}>
          {menuCategories.map((category) => {
            const isOpen = openKey === category.key;
            return (
              <SubMenuItem key={category.key}>
                <CategoryButton
                  type="button"
                  $active={isOpen}
                  aria-expanded={isOpen}
                  onClick={() => toggleOpen(category)}
                >
                  {category.title}
                </CategoryButton>
              </SubMenuItem>
            );
          })}
        </ScrollContainer>
      </Content>
    </Layout>
  );
}

export default memo(MenuBar);
